import tokens from '@kluein/design-tokens';
import { Box, Button, Dialog, DialogType } from '@kluein/klue-ui';
import {
  Check,
  CloudArrowUp,
  ListNumbers,
  PlusCircle,
} from '@phosphor-icons/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { StringParam, useQueryParams } from 'use-query-params';

import { BoardWizard } from 'components/common/board-wizard/BoardWizard';
import { CreateRivalsNotifier } from 'components/common/board-wizard/partials/CreateRivalsNotifier';
import { useAuth } from 'contexts/auth';
import { useCompanyFeatureFlags } from 'hooks/use-company-feature-flags';
import { useNotifier, NotifierType } from 'hooks/use-notifier';
import { useResponsive } from 'hooks/use-responsive';
import { APP_V1_BASEURL } from 'lib/urls';
import { DASHBOARD_DESKTOP_MAX_WIDTH } from 'pages/dashboard/dashboard.constants';
import TRACKING_IDS from 'tracking-ids';

import ViewAsDropdown from './partials/ViewAsDrop';

import type { DashboardSortType } from 'api/endpoints/rivals.types';
import type { Dispatch } from 'store/store.types';

type ButtonProps = {
  iconOnly?: boolean;
};

function NewBoardButton({ iconOnly }: ButtonProps) {
  const { disableNewCreateBoard, isV2BoardWizardEnabled } =
    useCompanyFeatureFlags('disableNewCreateBoard', 'isV2BoardWizardEnabled');
  const { t } = useTranslation();

  const [isOpen, setOpen] = useState(false);
  const [, setQuery] = useQueryParams({ create: StringParam });

  let additionalProps: { onClick?: () => void; href?: string };
  // Default (flag absent) is the new modal; `disableNewCreateBoard: true`
  // keeps a tenant on an older flow. Order matters: opted-out tenants
  // with `isV2BoardWizardEnabled` fall through to the v2 BoardWizard.
  if (!disableNewCreateBoard) {
    additionalProps = {
      onClick: () => setQuery({ create: 'board' }, 'replaceIn'),
    };
  } else if (isV2BoardWizardEnabled) {
    additionalProps = { onClick: () => setOpen(true) };
  } else {
    additionalProps = { href: APP_V1_BASEURL + '/dashboard/edit#new' };
  }

  return (
    <>
      <Button
        variant="flat"
        label={iconOnly ? undefined : t('Dashboard:curatorControls.newBoard')}
        leftSlot={<PlusCircle />}
        tip={
          iconOnly
            ? { content: t('Dashboard:curatorControls.newBoard') }
            : undefined
        }
        {...additionalProps}
        data-tracking-id={TRACKING_IDS.dashboard.curatorControls.newBoard}
      />
      {isOpen && disableNewCreateBoard && (
        <BoardWizard
          allowBlank
          useTemplatePicker
          onClose={() => setOpen(false)}
        />
      )}
      {(isV2BoardWizardEnabled || !disableNewCreateBoard) && (
        <CreateRivalsNotifier />
      )}
    </>
  );
}

function EditGroupsButton({ iconOnly }: ButtonProps) {
  const { t } = useTranslation();
  return (
    <Button
      variant="flat"
      label={iconOnly ? undefined : t('Dashboard:curatorControls.editGroups')}
      leftSlot={<ListNumbers />}
      tip={
        iconOnly
          ? { content: t('Dashboard:curatorControls.editGroups') }
          : undefined
      }
      href={APP_V1_BASEURL + '/dashboard/edit'}
      data-tracking-id={TRACKING_IDS.dashboard.curatorControls.editGroups}
    />
  );
}

type SaveDefaultsButtonProps = ButtonProps &
  Pick<CuratorControlsProps, 'sort' | 'group'>;
function SaveDefaultsButton({
  iconOnly,
  sort,
  group,
}: SaveDefaultsButtonProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch<Dispatch>();
  const user = useAuth().user!;
  const [showSuccess, setShowSuccess] = useState(false);
  const { notify } = useNotifier();

  async function handleSaveDefaults() {
    try {
      await dispatch.auth.updateFeatureFlag({
        path: ['defaults', 'dashboard'],
        value: {
          sortBy: sort || 'alpha',
          activeGroupId: group || 0,
          curatorId: user.id,
          updatedAt: new Date().toISOString(),
        },
      });
      setShowSuccess(true);
    } catch (error) {
      notify({
        type: NotifierType.ERROR,
        message: t('Dashboard:curatorControls.saveDefaultsError.message'),
      });
    }
  }

  return (
    <>
      <Button
        variant="flat"
        label={
          iconOnly ? undefined : t('Dashboard:curatorControls.saveDefaults')
        }
        leftSlot={<CloudArrowUp />}
        tip={
          iconOnly
            ? { content: t('Dashboard:curatorControls.saveDefaults') }
            : undefined
        }
        onClick={handleSaveDefaults}
        data-tracking-id={TRACKING_IDS.dashboard.curatorControls.saveDefaults}
      />
      <Dialog
        position="center"
        type={DialogType.SUCCESS}
        show={showSuccess}
        icon={<Check />}
        title={t('Dashboard:curatorControls.saveDefaultsSuccess.title')}
        onClose={() => setShowSuccess(false)}
        action={() => setShowSuccess(false)}
        actionLabel={t('Common:actions.ok')}
        showCancelButton={false}
        showActionButton
        description={t(
          'Dashboard:curatorControls.saveDefaultsSuccess.description',
        )}
      />
    </>
  );
}

type CuratorControlsProps = {
  sort?: DashboardSortType;
  group?: number;
};
export function CuratorControls({ sort, group }: CuratorControlsProps) {
  const {
    maxWidth: { small: isSmall },
  } = useResponsive();
  return (
    <Box
      fill="horizontal"
      flex={false}
      border={{ side: 'bottom', color: tokens.color.neutral[400] }}
    >
      <Box
        fill="horizontal"
        alignSelf="center"
        direction="row"
        width={{
          max: DASHBOARD_DESKTOP_MAX_WIDTH,
        }}
        pad={{ start: 'xxlarge', vertical: 'xsmall' }}
        align="center"
      >
        <NewBoardButton iconOnly={isSmall} />
        <EditGroupsButton iconOnly={isSmall} />
        <SaveDefaultsButton iconOnly={isSmall} sort={sort} group={group} />
        <ViewAsDropdown iconOnly={isSmall} />
      </Box>
    </Box>
  );
}
